import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Brain,
  Sparkles,
  Search,
  PenTool,
  Target,
  BookOpen,
  Calendar,
  TrendingUp,
  Zap,
  Award,
  Lightbulb,
  Rocket,
  Palette,
  UserCheck,
  BarChart3,
  User,
  Calculator,
  Activity,
  Video,
  Users as UsersGroup
} from 'lucide-react';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { Badge } from '../components/shared/Badge';

interface AIFeature {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  path: string;
  status: 'available' | 'coming-soon' | 'beta';
  color: 'primary' | 'secondary' | 'accent';
  benefits: string[];
}

const aiFeatures: AIFeature[] = [
  {
    id: 'essay-editor',
    title: 'AI Essay Editor',
    description: 'Get real-time feedback on your college essays with advanced AI analysis for structure, language, and authenticity.',
    icon: PenTool,
    path: '/essays',
    status: 'available',
    color: 'primary',
    benefits: ['Real-time feedback', 'Structure analysis', 'Grammar suggestions', 'Authenticity scoring']
  },
  {
    id: 'research-finder',
    title: 'Research Opportunities',
    description: 'Discover personalized research opportunities that match your interests and academic level.',
    icon: Search,
    path: '/research',
    status: 'available',
    color: 'secondary',
    benefits: ['Personalized matching', 'Deadline tracking', 'Application guidance', 'Merit scoring']
  },
  {
    id: 'summer-programs',
    title: 'Summer Programs',
    description: 'Find the perfect summer internships, camps, and programs to boost your college applications.',
    icon: Target,
    path: '/summer',
    status: 'beta',
    color: 'accent',
    benefits: ['Smart recommendations', 'Cost analysis', 'Scholarship alerts', 'Application tracking']
  },
  {
    id: 'theme-builder',
    title: 'Application Theme Builder',
    description: 'Discover your unique story and build compelling application narratives that stand out to admissions officers.',
    icon: Palette,
    path: '/theme-builder',
    status: 'available',
    color: 'primary',
    benefits: ['Narrative discovery', 'Theme analysis', 'Story strengthening', 'College alignment']
  },
  {
    id: 'expert-advisors',
    title: 'Expert Advisor Matching',
    description: 'Connect with top college admissions experts personalized to your goals, interests, and specific needs.',
    icon: UserCheck,
    path: '/advisors',
    status: 'available',
    color: 'secondary',
    benefits: ['Personalized matching', 'Expert guidance', 'Flexible communication', 'Proven success rates']
  },
  {
    id: 'university-insights',
    title: 'University Insights & Trends',
    description: 'Real-time data and AI analysis of competitive university admission trends and statistics.',
    icon: BarChart3,
    path: '/university-insights',
    status: 'available',
    color: 'accent',
    benefits: ['Real-time data', 'Admission trends', 'Competitive analysis', 'Strategic insights']
  },
  {
    id: 'brand-analyzer',
    title: 'Personal Brand Analyzer',
    description: 'AI-powered analysis of your unique story and personal brand to strengthen college applications.',
    icon: User,
    path: '/brand-analyzer',
    status: 'available',
    color: 'primary',
    benefits: ['Brand analysis', 'Story coherence', 'Differentiation tips', 'Narrative building']
  },
  {
    id: 'admission-calculator',
    title: 'Admission Probability Calculator',
    description: 'Get personalized admission probability predictions using advanced AI algorithms and data.',
    icon: Calculator,
    path: '/admission-calculator',
    status: 'available',
    color: 'secondary',
    benefits: ['Probability predictions', 'Factor analysis', 'Safety/match/reach', 'Data-driven insights']
  },
  {
    id: 'trends-tracker',
    title: 'Real-time Admission Trends',
    description: 'Live tracking of college admission trends, policy changes, and important updates.',
    icon: Activity,
    path: '/trends-tracker',
    status: 'available',
    color: 'accent',
    benefits: ['Live updates', 'Trend analysis', 'Policy alerts', 'Deadline tracking']
  },
  {
    id: 'interview-prep',
    title: 'AI Interview Preparation',
    description: 'Practice college interviews with AI-powered feedback and personalized coaching.',
    icon: Video,
    path: '/interview-prep',
    status: 'available',
    color: 'primary',
    benefits: ['Mock interviews', 'AI feedback', 'Performance analysis', 'Confidence building']
  },
  {
    id: 'family-collaboration',
    title: 'Family Collaboration Hub',
    description: 'Coordinate college preparation with parents, students, and counselors in one platform.',
    icon: UsersGroup,
    path: '/family-collaboration',
    status: 'available',
    color: 'secondary',
    benefits: ['Family coordination', 'Shared tasks', 'Progress tracking', 'Communication tools']
  },
  {
    id: 'college-matching',
    title: 'College Match AI',
    description: 'Get personalized college recommendations based on your profile, preferences, and goals.',
    icon: BookOpen,
    path: '/colleges',
    status: 'coming-soon',
    color: 'primary',
    benefits: ['Perfect fit analysis', 'Admission probability', 'Financial fit scoring', 'Cultural matching']
  },
  {
    id: 'career-guidance',
    title: 'Career Path AI',
    description: 'Explore career paths aligned with your interests and get personalized guidance for your future.',
    icon: TrendingUp,
    path: '/career',
    status: 'coming-soon',
    color: 'secondary',
    benefits: ['Career exploration', 'Skills analysis', 'Pathway planning', 'Industry insights']
  },
  {
    id: 'schedule-optimizer',
    title: 'Study Schedule AI',
    description: 'Optimize your study schedule with AI-powered time management and productivity insights.',
    icon: Calendar,
    path: '/schedule',
    status: 'coming-soon',
    color: 'accent',
    benefits: ['Smart scheduling', 'Productivity tracking', 'Goal alignment', 'Stress optimization']
  }
];

export const AIHub: React.FC = () => {
  const navigate = useNavigate();

  const handleFeatureClick = (feature: AIFeature) => {
    if (feature.status === 'available' || feature.status === 'beta') {
      navigate(feature.path);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'beta': return 'warning';
      case 'coming-soon': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'available': return 'Available';
      case 'beta': return 'Beta';
      case 'coming-soon': return 'Coming Soon';
      default: return status;
    }
  };

  return (
    <div className="space-y-8">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center relative overflow-hidden"
      >
        <div className="relative z-10">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center">
                <Brain className="w-10 h-10 text-white" />
              </div>
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                className="absolute -inset-2 bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500 rounded-2xl opacity-20 blur-xl"
              />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            AI-Powered College Prep
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Harness the power of artificial intelligence to accelerate your college preparation journey.
            Get personalized insights, recommendations, and guidance tailored specifically for you.
          </p>

          <div className="flex flex-wrap justify-center gap-4 mb-8">
            <Badge variant="primary" className="px-4 py-2">
              <Sparkles size={16} className="mr-2" />
              Smart Recommendations
            </Badge>
            <Badge variant="secondary" className="px-4 py-2">
              <Zap size={16} className="mr-2" />
              Real-time Analysis
            </Badge>
            <Badge variant="accent" className="px-4 py-2">
              <Award size={16} className="mr-2" />
              Personalized Guidance
            </Badge>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-primary-100 rounded-full opacity-20" />
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-accent-100 rounded-full opacity-20" />
        </div>
      </motion.div>

      {/* AI Features Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
      >
        {aiFeatures.map((feature, index) => {
          const Icon = feature.icon;
          const isClickable = feature.status === 'available' || feature.status === 'beta';

          return (
            <motion.div
              key={feature.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 * index }}
              whileHover={isClickable ? { y: -4 } : {}}
              className={isClickable ? 'cursor-pointer' : ''}
              onClick={() => handleFeatureClick(feature)}
            >
              <Card
                hover={isClickable}
                className={`h-full ${!isClickable ? 'opacity-75' : ''} group`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className={`p-3 rounded-xl bg-${feature.color}-100 group-hover:scale-110 transition-transform`}>
                    <Icon className={`w-6 h-6 text-${feature.color}-600`} />
                  </div>
                  <Badge variant={getStatusColor(feature.status) as any} size="sm">
                    {getStatusLabel(feature.status)}
                  </Badge>
                </div>

                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  {feature.title}
                </h3>

                <p className="text-gray-600 mb-4 line-height-relaxed">
                  {feature.description}
                </p>

                <div className="space-y-2 mb-6">
                  <h4 className="text-sm font-semibold text-gray-700">Key Benefits:</h4>
                  <ul className="space-y-1">
                    {feature.benefits.map((benefit, idx) => (
                      <li key={idx} className="text-sm text-gray-600 flex items-center">
                        <span className={`w-1.5 h-1.5 bg-${feature.color}-400 rounded-full mr-2`} />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="mt-auto">
                  {isClickable ? (
                    <Button
                      variant="outline"
                      className="w-full group-hover:bg-primary-50 group-hover:border-primary-300"
                    >
                      {feature.status === 'beta' ? 'Try Beta' : 'Get Started'}
                    </Button>
                  ) : (
                    <Button variant="ghost" disabled className="w-full">
                      Coming Soon
                    </Button>
                  )}
                </div>
              </Card>
            </motion.div>
          );
        })}
      </motion.div>

      {/* Stats Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6 }}
      >
        <Card className="bg-gradient-to-r from-primary-500 to-accent-500 text-white">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">AI-Powered Insights</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="text-3xl font-bold mb-2">95%</div>
                <p className="text-primary-100">Essay improvement rate</p>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">1000+</div>
                <p className="text-primary-100">Opportunities discovered</p>
              </div>
              <div>
                <div className="text-3xl font-bold mb-2">24/7</div>
                <p className="text-primary-100">AI assistance available</p>
              </div>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8 }}
        className="text-center"
      >
        <Card className="bg-gray-50">
          <div className="max-w-2xl mx-auto">
            <Lightbulb className="w-12 h-12 text-primary-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Supercharge Your College Prep?
            </h2>
            <p className="text-gray-600 mb-6">
              Start with our AI Essay Editor to get instant feedback on your writing,
              then explore research opportunities perfectly matched to your interests.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                onClick={() => navigate('/essays')}
                className="flex items-center space-x-2"
              >
                <PenTool size={20} />
                <span>Start Writing Essays</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/research')}
                className="flex items-center space-x-2"
              >
                <Search size={20} />
                <span>Find Research Opportunities</span>
              </Button>
            </div>
          </div>
        </Card>
      </motion.div>
    </div>
  );
};